// src/components/BankReconciliation.js
// Bank Reconciliation (BC-style, manual check-off + post & lock): pick a bank,
// enter the statement's closing date + balance, tick the ledger entries that
// appear on the statement, and post when the difference is zero. Posting marks
// those entries cleared so they don't reappear next time.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, MenuItem, Alert, CircularProgress, IconButton, Button,
  Checkbox, Grid, Divider, Chip
} from '@mui/material';
import { Home, AccountBalanceWallet, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const todayStr = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString();

const BankReconciliation = () => {
  const navigate = useNavigate();
  const [banks, setBanks]             = useState([]);
  const [bankAccountNo, setBank]      = useState('');
  const [statementDate, setStmtDate]  = useState(todayStr());
  const [statementBalance, setStmtBal] = useState('');
  const [worksheet, setWorksheet]     = useState(null);
  const [selected, setSelected]       = useState(() => new Set());
  const [history, setHistory]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [posting, setPosting]         = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  useEffect(() => {
    axios.get(`${API}/api/finance/banks`, { headers: hdrs() })
      .then(r => setBanks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const loadHistory = useCallback((acc) => {
    axios.get(`${API}/api/finance/bank-recon`, { headers: hdrs(), params: { bankAccountNo: acc } })
      .then(r => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => setHistory([]));
  }, []);

  const loadWorksheet = useCallback(async () => {
    if (!bankAccountNo) { setError('Select a bank account'); return; }
    setLoading(true); setError(''); setSuccess(''); setWorksheet(null); setSelected(new Set());
    try {
      const { data } = await axios.get(`${API}/api/finance/bank-recon/worksheet`,
        { headers: hdrs(), params: { bankAccountNo } });
      setWorksheet(data);
      loadHistory(bankAccountNo);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load worksheet');
    } finally { setLoading(false); }
  }, [bankAccountNo, loadHistory]);

  const toggle = (entryNo) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(entryNo)) next.delete(entryNo); else next.add(entryNo);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(prev => {
      if (!worksheet) return prev;
      if (prev.size === worksheet.unclearedEntries.length) return new Set();
      return new Set(worksheet.unclearedEntries.map(e => e.entryNo));
    });
  };

  // Live figures
  const selectedSigned = worksheet
    ? worksheet.unclearedEntries.filter(e => selected.has(e.entryNo)).reduce((s, e) => s + e.signed, 0)
    : 0;
  const clearedBalance = worksheet ? worksheet.openingClearedBalance + selectedSigned : 0;
  const stmtBalNum = statementBalance === '' ? null : Number(statementBalance);
  const difference = stmtBalNum === null ? null : stmtBalNum - clearedBalance;
  const balanced = worksheet && stmtBalNum !== null && selected.size > 0 && Math.abs(difference) < 0.01;

  const post = async () => {
    if (!balanced || posting) return;
    setPosting(true); setError(''); setSuccess('');
    try {
      const { data } = await axios.post(`${API}/api/finance/bank-recon`, {
        bankAccountNo, statementDate, statementBalance: stmtBalNum,
        clearedEntryNos: Array.from(selected),
      }, { headers: hdrs() });
      setSuccess(`Reconciliation ${data.reconciliation?.reconciliationNo || ''} posted.`);
      await loadWorksheet();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post reconciliation');
    } finally { setPosting(false); }
  };

  const diffColor = difference === null ? 'text.primary' : Math.abs(difference) < 0.01 ? 'success.main' : 'error.main';

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={4}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <AccountBalanceWallet sx={{ mr: 1, color: '#00838f' }} />
              <Typography variant="h5" sx={{ color: '#00838f' }} fontWeight="bold">Bank Reconciliation</Typography>
            </Box>
            <IconButton onClick={() => navigate('/finance')} title="Finance Home"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
          </Box>

          {/* ── Controls ── */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField select label="Bank Account" value={bankAccountNo} size="small" fullWidth
                onChange={e => { setBank(e.target.value); setWorksheet(null); setSelected(new Set()); }}>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {banks.map(b => <MenuItem key={b.accountNo} value={b.accountNo}>{b.bankName} — {b.accountName}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField type="date" label="Statement Date" value={statementDate} size="small" fullWidth
                onChange={e => setStmtDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField type="number" label="Statement Closing Balance" value={statementBalance} size="small" fullWidth
                onChange={e => setStmtBal(e.target.value)} inputProps={{ step: '0.01' }} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button variant="contained" onClick={loadWorksheet} disabled={loading || !bankAccountNo} fullWidth
                startIcon={loading ? <CircularProgress size={18} /> : null}>
                {loading ? 'Loading…' : 'Load'}
              </Button>
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {worksheet && (
            <>
              {/* ── Reconciliation summary ── */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Opening Cleared</Typography>
                    <Typography variant="subtitle1" fontWeight="bold">{sym()} {fmt(worksheet.openingClearedBalance)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Cleared Balance</Typography>
                    <Typography variant="subtitle1" fontWeight="bold">{sym()} {fmt(clearedBalance)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Statement Balance</Typography>
                    <Typography variant="subtitle1" fontWeight="bold">{stmtBalNum === null ? '—' : `${sym()} ${fmt(stmtBalNum)}`}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 1.5, bgcolor: balanced ? 'success.light' : 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Difference</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: diffColor }}>
                      {difference === null ? '—' : `${sym()} ${fmt(difference)}`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {worksheet.unclearedEntries.length} uncleared entries · {selected.size} selected · book balance {sym()} {fmt(worksheet.bookBalance)}
                </Typography>
                <Button variant="contained" color="success" onClick={post} disabled={!balanced || posting}
                  startIcon={posting ? <CircularProgress size={18} /> : <CheckCircle />}>
                  {posting ? 'Posting…' : 'Post Reconciliation'}
                </Button>
              </Box>

              {/* ── Entries to match ── */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell padding="checkbox" sx={{ bgcolor: 'primary.main' }}>
                        <Checkbox
                          sx={{ color: 'white' }}
                          checked={worksheet.unclearedEntries.length > 0 && selected.size === worksheet.unclearedEntries.length}
                          indeterminate={selected.size > 0 && selected.size < worksheet.unclearedEntries.length}
                          onChange={toggleAll}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Document</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Deposit</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Withdrawal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {worksheet.unclearedEntries.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No uncleared entries — this account is fully reconciled.</TableCell></TableRow>
                    ) : worksheet.unclearedEntries.map(e => {
                      const isSel = selected.has(e.entryNo);
                      return (
                        <TableRow key={e.entryNo} hover selected={isSel} onClick={() => toggle(e.entryNo)}
                          sx={{ cursor: 'pointer' }}>
                          <TableCell padding="checkbox"><Checkbox checked={isSel} onChange={() => toggle(e.entryNo)} /></TableCell>
                          <TableCell>{fmtDate(e.date)}</TableCell>
                          <TableCell>{e.documentNo}</TableCell>
                          <TableCell>{e.description}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>{e.type === 'Credit' ? fmt(e.amount) : ''}</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>{e.type === 'Debit' ? fmt(e.amount) : ''}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              {/* ── History ── */}
              {history.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>Past Reconciliations</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Ref</strong></TableCell>
                        <TableCell><strong>Statement Date</strong></TableCell>
                        <TableCell align="right"><strong>Statement Balance</strong></TableCell>
                        <TableCell align="right"><strong>Cleared Balance</strong></TableCell>
                        <TableCell align="right"><strong>Entries</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map(h => (
                        <TableRow key={h.reconciliationNo} hover>
                          <TableCell>{h.reconciliationNo}</TableCell>
                          <TableCell>{fmtDate(h.statementDate)}</TableCell>
                          <TableCell align="right">{fmt(h.statementBalance)}</TableCell>
                          <TableCell align="right">{fmt(h.clearedBalance)}</TableCell>
                          <TableCell align="right">{h.clearedCount}</TableCell>
                          <TableCell align="center"><Chip label={h.status} size="small" color="success" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BankReconciliation;
